
import { Invoice } from "@/types/payments";
import { InvoiceApiClient } from "@/clients/invoiceApiClient";

export class InvoiceService {
  
  /**
   * Get all invoices
   * @param onlyApproved Whether to return only approved invoices
   * @returns Array of invoices
   */
  public async getInvoices(onlyApproved: boolean = false): Promise<Invoice[]> {
    console.log("InvoiceService: Getting invoices", onlyApproved ? "(approved only)" : "");
    const { invoices } = await InvoiceApiClient.getInvoices(onlyApproved);
    console.log("InvoiceService: Retrieved invoices:", invoices.length);
    return invoices;
  }

  /**
   * Get all approved invoices
   * @returns Array of approved invoices
   */
  public async getApprovedInvoices(): Promise<Invoice[]> {
    console.log("InvoiceService: Getting approved invoices");
    const { invoices } = await InvoiceApiClient.getApprovedInvoices();
    console.log("InvoiceService: Retrieved approved invoices:", invoices.length);
    return invoices;
  }

  public async getPendingInvoices(): Promise<Invoice[]> {
    const { invoices } = await InvoiceApiClient.getPendingInvoices();
    return invoices;
  }

  public async approveInvoice(invoiceId: string): Promise<{ success: boolean; invoice?: Invoice }> {
    return await InvoiceApiClient.approveInvoice(invoiceId);
  }

  public async updateInvoiceStatus(
    invoiceId: string, 
    status: "pending" | "paid"
  ): Promise<{ success: boolean; invoice?: Invoice }> {
    return await InvoiceApiClient.updateInvoiceStatus(invoiceId, status);
  }
  
  public async getDocumentFile(documentId: string): Promise<{ filePath: string; fileName: string | null } | null> {
    return await InvoiceApiClient.getDocumentFile(documentId);
  }

  public async getInvoiceFileUrl(filePath: string): Promise<string | null> {
    console.log("InvoiceService: Getting file URL for:", filePath);
    return await InvoiceApiClient.getInvoiceFileUrl(filePath);
  }

  /**
   * Download an invoice file from storage
   * @param filePath Path to the file in storage
   * @param fileName Name of the file to download
   * @returns The file data and any error
   */
  public async downloadInvoiceFile(filePath: string, fileName: string): Promise<{ data?: Blob; error?: Error }> {
    try {
      console.log("InvoiceService: Downloading file:", filePath);
      
      // Get the signed URL for the file
      const fileUrl = await this.getInvoiceFileUrl(filePath);
      
      if (!fileUrl) {
        return { error: new Error("Could not get file URL") };
      }
      
      // Fetch the file from the signed URL
      const response = await fetch(fileUrl);
      
      if (!response.ok) {
        return { error: new Error(`Failed to download file: ${response.statusText}`) };
      }
      
      // Convert the response to a blob
      const blob = await response.blob();
      return { data: blob };
    } catch (error) {
      console.error("Error downloading invoice file:", error);
      return { error: error instanceof Error ? error : new Error("Unknown error downloading file") };
    }
  }
}
